import { useKeycloak, ReactKeycloakProvider } from '@react-keycloak/web';
import { useLocalStorage } from '@mantine/hooks';
import * as React from 'react';

import { getTodayDate, getUserRoles } from '../utils/common';
import ApiClient, { client } from '../utils/api-client';
import * as auth from '../auth-provider';
import ROLES from '../utils/roles';

import { requestForToken } from '../config/firebase.config';
import { useNavigate } from 'react-router-dom';

/**
 * Location server
 */
const clientApi = new ApiClient({ baseUrl: process.env.REACT_APP_LOCATION_URL });

async function bootstrapAppData() {
  try {
    // Get system user using login user token
    /** System user */
    const user = await client.get('/system-user/user');

    const locData = localStorage.getItem('regions');
    if (!locData) {
      /** Fills up localstorage with location information.*/
      const regions = await clientApi.get('/locations');
      localStorage.setItem('regions', JSON.stringify(regions));
    }

    if (window.__dev__) console.log(user?.data);
    return await user?.data;
  } catch (err) {
    if (window.__dev__) console.log(err);
    throw new Error();
  }
}

const AuthContext = React.createContext();
AuthContext.displayName = 'AuthContext';

function AuthProvider(props) {
  const { keycloak, initialized } = useKeycloak();
  // Localstorage hook
  const [role, setRole] = useLocalStorage({ key: 'user-role' });
  // Use local storage for export numbers file count
  const [date, setDate] = useLocalStorage({ key: 'date' });
  const [exportNumbersCount, setExportNumbersCount] = useLocalStorage({ key: 'exp_num_cnt' });

  const [user, setUser] = React.useState({});
  const [fcmToken, setFcmToken] = React.useState('');
  const [systemUser, setSystemUser] = React.useState({});
  const [isProfilePicUpdated, setIsProfilePicUpdated] = React.useState(false);
  // When FCM Notificaiton received, update all notifications
  const [notificationReceived, setNotificationReceived] = React.useState(false);
  // For sidebar unread notification indicator
  const [unreadNotificationCount, setUnreadNotificationCount] = React.useState(0);

  /**Get token from keycloak */
  const token = React.useMemo(() => keycloak.token, [keycloak.token]);

  React.useEffect(() => {
    const exportNumberDate = getTodayDate('YYYYMMDD');

    keycloak.loadUserProfile().then(usr => setUser({ ...user, ...usr, __roles: keycloak.realmAccess.roles }));

    // Get user roles
    const userRoles = getUserRoles(keycloak?.realmAccess?.roles);

    // set Role if it is not store in local storage and user have only one role
    if (!role && userRoles.length === 1) setRole(userRoles[0]);
    // Clean up
    // This event listener is used to prevent user to change role in local storage
    window.addEventListener('storage', roleChangeListener);

    // Set values of export numbers in local storage incase of empty value
    if (!date || date !== exportNumberDate) {
      setDate(exportNumberDate);
      setExportNumbersCount('01');
    }

    return () => window.removeEventListener('storage', roleChangeListener);
  }, []);

  React.useEffect(() => {
    // Set system user
    bootstrapAppData()
      .then(user => {
        setSystemUser(user);
        checkFCMToken(user);
      })
      .catch(err => setSystemUser(0));
  }, [isProfilePicUpdated]);

  // Get Unread notification count
  React.useEffect(() => {
    systemUser?._id && getUnreadNotificationCount();
  }, [notificationReceived, systemUser?._id]);

  const getUnreadNotificationCount = React.useCallback(async () => {
    try {
      const res = await client.get(`/notifications?$filter=receivers eq {"notification_id":"${systemUser?._id?.toString()}", "is_viewed":${false}}`);
      console.log(res?.data?.length);
      setUnreadNotificationCount(res?.data?.length || 0);
    } catch (err) {
      if (window.__dev__) console.log(err);
    }
  }, [systemUser?._id]);

  /**
   * This function is used to check and update fcm token in user profile
   */
  const checkFCMToken = React.useCallback(async user => {
    // FCM Token
    const token = await requestForToken();
    setFcmToken(token);
    if (window.__dev__) console.log('FCM Token => ', token);
    if (window.__dev__) console.log('User Token => ', user?.fcm_token);
    if (user?.fcm_token !== token)
      client.put(`/system-user?id=${user?._id}&keycloak=${user?.keycloak}`, { fcm_token: token }).then(res => {
        if (window.__dev__) console.log('FCM Token Updated: ', res);
      });
  });

  /**
   * This function is used to prevent user from changing role in local storage
   */
  const roleChangeListener = React.useCallback(e => {
    // Return incase there is not change in user-role
    if (e.key !== 'user-role') return;

    // GET User Roles
    const userRoles = getUserRoles(keycloak?.realmAccess?.roles);
    // Compare new role with user roles
    const newRole = !!userRoles?.includes(e?.newValue?.replace(/['"]+/g, ''));

    // Change user role if it is valid else logout user from system
    if (newRole) {
      setUserRole(e.newValue.replace(/['"]+/g, ''));
    } else logout();
  });

  /**
   * Logout function
   */
  const logout = React.useCallback(() => {
    keycloak.logout();
    ['regions', 'user-role', 'active-tab'].forEach(e => localStorage.removeItem(e));
  });

  /**
   * This function is used to set user role
   */
  const setUserRole = React.useCallback(role => {
    setRole(role);
  });

  /**
   * This function is use to set notification receive value
   */
  const setNotificationUpdate = React.useCallback(() => {
    setNotificationReceived(prev => !prev);
  });

  /**
   * This function is use to update isProfilePicUpdated state
   */
  const setPictureUpdated = React.useCallback(() => {
    setIsProfilePicUpdated(prev => !prev);
  });

  const value = React.useMemo(
    () => ({
      logout,
      user,
      fcmToken,
      systemUser,
      token,
      role,
      setUserRole,
      setNotificationUpdate,
      notificationReceived,
      unreadNotificationCount,
      setPictureUpdated,
      isProfilePicUpdated,
    }),
    [
      logout,
      role,
      fcmToken,
      systemUser,
      user,
      token,
      setUserRole,
      setNotificationUpdate,
      notificationReceived,
      unreadNotificationCount,
      setPictureUpdated,
      isProfilePicUpdated,
    ]
  );

  return <AuthContext.Provider value={value} {...props}></AuthContext.Provider>;
}
function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
}

export { AuthProvider, useAuth };
